import type { components } from "@sagot/api-client";
import { SSO_DOMAINS_MAX, SSO_METADATA_MAX_BYTES, SSO_PUBLIC_MAIL_DOMAINS, type StaffSsoKind } from "@sagot/constants";

export type StaffSso = components["schemas"]["StaffSsoOut"];
export type StaffSsoBody = components["schemas"]["StaffSsoIn"];

/** What the form edits; mirrors the PUT body of `/v1/staff-sso`. */
export interface SsoDraft {
  kind: StaffSsoKind;
  metadataXml: string;
  metadataName: string;
  issuer: string;
  clientId: string;
  clientSecret: string;
  domains: string[];
  enforce: boolean;
  breakGlassUserId: string;
}

export function draftOf(sso: StaffSso | undefined): SsoDraft {
  return {
    kind: sso?.kind === "oidc" ? "oidc" : "saml",
    metadataXml: "",
    metadataName: "",
    issuer: sso?.oidc?.issuer ?? "",
    clientId: sso?.oidc?.client_id ?? "",
    clientSecret: "",
    domains: [...(sso?.email_domains ?? [])],
    enforce: sso?.enforce ?? false,
    breakGlassUserId: sso?.break_glass?.id ?? sso?.owners[0]?.id ?? "",
  };
}

/** Request body for the draft; a blank metadata file / secret is left out so the stored one stays. */
export function bodyOf(draft: SsoDraft): StaffSsoBody {
  const body: StaffSsoBody = { kind: draft.kind, email_domains: draft.domains, enforce: draft.enforce };
  if (draft.enforce && draft.breakGlassUserId) body.break_glass_user_id = draft.breakGlassUserId;
  if (draft.kind === "saml") {
    if (draft.metadataXml) body.metadata_xml = draft.metadataXml;
  } else {
    body.issuer = draft.issuer.trim();
    body.client_id = draft.clientId.trim();
    if (draft.clientSecret) body.client_secret = draft.clientSecret;
  }
  return body;
}

const DOMAIN = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

/** A typed domain, lower-cased and stripped of `@`, or `null` when it is not a domain name. */
export function parseSsoDomain(raw: string): string | null {
  const value = raw.trim().toLowerCase().replace(/^@/, "");
  return DOMAIN.test(value) ? value : null;
}

export const isPublicMailDomain = (domain: string): boolean => (SSO_PUBLIC_MAIL_DOMAINS as readonly string[]).includes(domain);

export type SsoProblem = "metadata" | "issuer" | "clientId" | "clientSecret" | "domains";

/** The first fields that block saving (the API checks the rest). */
export function problemsOf(draft: SsoDraft, sso: StaffSso | undefined): SsoProblem[] {
  const found: SsoProblem[] = [];
  const stored = sso?.kind === draft.kind;
  if (draft.kind === "saml" && !draft.metadataXml && !(stored && sso.saml)) found.push("metadata");
  if (draft.kind === "oidc") {
    if (!draft.issuer.trim()) found.push("issuer");
    if (!draft.clientId.trim()) found.push("clientId");
    if (!draft.clientSecret && !(stored && sso.oidc?.has_secret)) found.push("clientSecret");
  }
  if (draft.domains.length === 0 || draft.domains.length > SSO_DOMAINS_MAX) found.push("domains");
  return found;
}

export const metadataTooLarge = (size: number): boolean => size > SSO_METADATA_MAX_BYTES;

/** Enforce can only be switched on after a passing test (or stays on when it already is). */
export const canEnforce = (sso: StaffSso | undefined): boolean => sso?.enforce === true || Boolean(sso?.last_tested_at);

const REASONS = ["failed", "unavailable", "not_a_member", "domain", "expired"] as const;
export type SsoReason = (typeof REASONS)[number];
export function asReason(value: string | null): SsoReason {
  return REASONS.find((reason) => reason === value) ?? "failed";
}
